/** @typedef {import('headlight-configurable-application').Pict} Pict */

const MaterialsCascadePicker = require('./PictProvider-InputType-MaterialsCascadePicker.js');
const ScopedEntityData = require('./_ScopedEntityData.js');
const libLabRouting = require('./_LabRouting.js');

/** @type {Record<string, any>} */
const _DEFAULT_CONFIGURATION =
{
	ProviderIdentifier: 'MaterialsLabPicker',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
	AutoSolveWithApp: false,
	Entity: 'Lab',
};

/**
 * The Lab picker (beast B4 — a JOIN cascade): the Picker-stack port of MaterialsLabSelector, mirroring
 * the legacy provider 1:1 on the MaterialsCascadePicker base. Multi-select by default; selecting labs
 * reconciles the sibling SampleLabJoin array (reuse-by-IDLab so server ids and downstream edits — e.g.
 * Received — survive re-selection). On the bound sample's SampleChanged it re-derives value + joins from
 * the DB; SampleLabSync (broadcast when a per-test lab changes) reconciles this selection with them; the
 * ProjectEntityDataRecords sweep projects the joins into _EntityData.
 */
class PictProviderInputTypeMaterialsLabPicker extends MaterialsCascadePicker
{
	/**
	 * @param {Object} pFable @param {Object} [pOptions] @param {string} [pServiceHash]
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DEFAULT_CONFIGURATION, pOptions), pServiceHash);
		/** @type {Pict & { TransactionTracking: any, TempData: any }} */
		this.pict;
	}

	/** Labs are customer-scoped (row-level auth), not project-scoped — the legacy hook only added the
	 *  Name sort on top of the shared pipeline. @param {Record<string, any>} pInput
	 *  @param {number|string|null} [pRowIndex] @param {Object} [pView] @return {{Filters: Array<string>, BackOffFilters: Array<string>}} */
	getContextualSearchFilters(pInput, pRowIndex, pView)
	{
		const tmpSplit = super.getContextualSearchFilters(pInput, pRowIndex, pView);
		tmpSplit.Filters.push('FSF~Name~ASC~0');
		return tmpSplit;
	}

	/** Init: legacy defaults (multi-select, Name EntityTag) + the configured family tracking GUID. */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this._applyLabDefaults(pInput);
		const tmpResult = super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		// sample is in charge of the family for tracking
		if (pInput.PictForm.SampleTrackingGUID && pInput.PictForm.SampleTrackingGUID != true)
		{
			this._setTrackingGUID(pInput, null, pInput.PictForm.SampleTrackingGUID);
		}
		return tmpResult;
	}

	/** Tabular twin of the init. */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this._applyLabDefaults(pInput);
		const tmpResult = super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		if (pInput.PictForm.SampleTrackingGUID && pInput.PictForm.SampleTrackingGUID != true)
		{
			this._setTrackingGUID(pInput, pRowIndex, pInput.PictForm.SampleTrackingGUID);
		}
		return tmpResult;
	}

	/** A sample can ship to several labs (multi-select), tag on Name. @param {Record<string, any>} pInput */
	_applyLabDefaults(pInput)
	{
		if (!pInput.PictForm)
		{
			pInput.PictForm = { };
		}
		if (pInput.PictForm.Multiple == null)
		{
			pInput.PictForm.Multiple = true;
		}
		if (pInput.PictForm.EntityTag == null)
		{
			pInput.PictForm.EntityTag = 'Name';
		}
	}

	/**
	 * Reconcile the selected Labs against the existing SampleLabJoins: reuse matches (by IDLab —
	 * preserving server ids + downstream edits), synthesize new (GUID minted off the document + salt +
	 * IDLab; saved-vs-unsaved sample keying), drop deselected. Ported 1:1.
	 * @param {Array<Record<string, any>>} pLabs @param {Array<Record<string, any>>} pCurrentSampleLabJoins
	 * @param {Record<string, any>|null} pSample @param {Object} pView @param {Record<string, any>} pInput
	 * @param {number|string|null} pRowIndex @return {Array<Record<string, any>>}
	 */
	_syncSampleLabJoins(pLabs, pCurrentSampleLabJoins, pSample, pView, pInput, pRowIndex)
	{
		const tmpUnclaimedJoins = Array.isArray(pCurrentSampleLabJoins) ? pCurrentSampleLabJoins.slice() : [ ];
		const tmpSampleLabJoins = [ ];
		for (const tmpLab of pLabs || [])
		{
			if (!tmpLab || typeof tmpLab !== 'object')
			{
				continue;
			}
			const tmpExistingJoinIndex = tmpUnclaimedJoins.findIndex((pJoin) => pJoin.IDLab == tmpLab.IDLab);
			if (tmpExistingJoinIndex >= 0)
			{
				tmpSampleLabJoins.push(tmpUnclaimedJoins[tmpExistingJoinIndex]);
				tmpUnclaimedJoins.splice(tmpExistingJoinIndex, 1);
				continue;
			}
			const tmpNewJoin = JSON.parse(JSON.stringify(pInput.PictForm.DefaultSampleLabJoinRecord ||
				{
					GUIDSampleLabJoin: `{~D:AppData.DocumentData.ReportData.GUIDDocument~}-SLJ-${this._getSalt(pView, pInput, pRowIndex)}`,
					IDLab: tmpLab.IDLab,
					Received: 0,
				}));
			tmpNewJoin.GUIDSampleLabJoin += `-${tmpLab.IDLab}`;
			if (pSample && pSample.GUIDSample)
			{
				if (pSample.IDSample > 0)
				{
					tmpNewJoin.IDSample = pSample.IDSample;
				}
				else
				{
					tmpNewJoin.GUIDSample = pSample.GUIDSample;
				}
			}
			this._translateRecordTemplateFields(tmpNewJoin);

			if (!tmpNewJoin.GUIDSampleLabJoin)
			{
				tmpNewJoin.GUIDSampleLabJoin = this.pict.getUUID();
			}
			tmpSampleLabJoins.push(tmpNewJoin);
		}
		return tmpSampleLabJoins;
	}

	/**
	 * A de-selected Lab takes its `SampleLabJoin` with it. Picker twin of the selector hook.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} pRowIndex
	 * @param {Set<string>} pRetainedIdentities
	 * @return {Array<{ Entity: string, Record: Record<string, any> }>}
	 */
	_collectRemovedDependentRecords(pView, pInput, pRowIndex, pRetainedIdentities)
	{
		const tmpRecordAddress = this._getRecordAddress(pInput, pRowIndex);
		const tmpJoinsAddress = pInput.PictForm.SampleLabJoinAddress || `${tmpRecordAddress}_SampleJoins`;
		const tmpJoins = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpJoinsAddress);
		if (!Array.isArray(tmpJoins))
		{
			return [ ];
		}
		const tmpRemovals = [ ];
		for (const tmpJoin of tmpJoins)
		{
			if (tmpJoin && typeof tmpJoin === 'object' && !pRetainedIdentities.has(String(tmpJoin.IDLab)))
			{
				tmpRemovals.push({ Entity: 'SampleLabJoin', Record: tmpJoin });
			}
		}
		return tmpRemovals;
	}

	/**
	 * The Lab cascade — the port of the legacy `_onRecordRefresh`: reconcile the SampleLabJoin array
	 * beside the record (clear → drop all joins), repaint dependents in-section, then super (the family
	 * bus fan-out). A cleared picker hands the seam `{}`/`[]` — normalized to "no labs" like the legacy's
	 * falsy-record branch.
	 * @param {Object} pView @param {Record<string, any>} pInput
	 * @param {Record<string, any>|Array<Record<string, any>>} pRecord
	 * @param {number|string} [pRowIndex] @param {string} [pTransactionGUID]
	 */
	_onEntityRecordCascade(pView, pInput, pRecord, pRowIndex, pTransactionGUID)
	{
		const tmpLabAddress = this._getRecordAddress(pInput, pRowIndex);
		const tmpLabs = Array.isArray(pRecord) ? pRecord :
			((pRecord && typeof pRecord === 'object' && Object.keys(pRecord).length > 0) ? [ pRecord ] : [ ]);
		const tmpSampleLabJoinsAddress = pInput.PictForm.SampleLabJoinAddress || `${tmpLabAddress}_SampleJoins`;
		let tmpSampleLabJoins = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleLabJoinsAddress);
		const tmpSample = this._getBoundSampleRecord(pView, pInput);
		if (!Array.isArray(tmpSampleLabJoins))
		{
			if (tmpSampleLabJoins)
			{
				this.log.error(`MaterialsLabPicker: SampleLabJoins at address ${tmpSampleLabJoinsAddress} is not an array`, { tmpSampleLabJoins });
			}
			tmpSampleLabJoins = [ ];
		}
		tmpSampleLabJoins = this._syncSampleLabJoins(tmpLabs, tmpSampleLabJoins, tmpSample, pView, pInput, pRowIndex);
		this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleLabJoinsAddress, tmpSampleLabJoins);
		// ensures any fields that use this record are updated immediately, if they are in the same section
		if (typeof pView.marshalToView === 'function')
		{
			pView.marshalToView();
		}
		super._onEntityRecordCascade(pView, pInput, pRecord, pRowIndex, pTransactionGUID);
	}

	/**
	 * Family + save-sweep events — ported 1:1 from the legacy `_handleEvent`:
	 *   - SampleChanged (tracking-matched): re-derive this selector from the DB — load the sample's
	 *     SampleLabJoins, land them at the join address, and commit the joined lab ids as the value
	 *     (the programmatic set re-runs the provider loop, so the join reconcile rides the same path a
	 *     user pick does). No/invalid sample resets the joins. The fence op is registered only when a
	 *     fetch actually runs (the legacy registered before its IDSample check and leaked the op —
	 *     unreachable-hang family, same reasoned-divergence lineage as B2's clear-reset).
	 *   - SampleLabSync (broadcast, no tracking): the per-test labs changed. Reconcile OUR value with
	 *     them (_LabRouting.syncSampleLabSelection) and commit — the programmatic set re-runs the
	 *     provider loop, so the join array derives itself.
	 *   - ProjectEntityDataRecords: project the joins (+ any sibling DocumentPolyJoin) into the sweep
	 *     target.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {any} pValue @param {string} pHTMLSelector
	 * @param {number|string|null} pRowIndex @param {string} pEvent @param {string} pTransactionGUID
	 */
	_handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		if (!pEvent || typeof pEvent !== 'string')
		{
			return;
		}

		const [ tmpEventType, tmpTrackingGUID, tmpEventPayload ] = pEvent.split(':');

		if (tmpEventType === 'SampleChanged')
		{
			if (tmpTrackingGUID !== this._getTrackingGUID(pInput, pRowIndex))
			{
				return;
			}
			if (!this._shouldRefreshSampleJoins(pInput))
			{
				return;
			}
			const tmpLabAddress = this._getRecordAddress(pInput, pRowIndex);
			const tmpSampleLabJoinAddress = pInput.PictForm.SampleLabJoinAddress || `${tmpLabAddress}_SampleJoins`;
			const tmpSampleRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpEventPayload);
			if (!tmpSampleRecord || typeof tmpSampleRecord !== 'object' || Array.isArray(tmpSampleRecord))
			{
				this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleLabJoinAddress, []);
				return;
			}
			if (!tmpSampleRecord.IDSample)
			{
				return;
			}
			const tmpRefreshEvent = 'SampleLabJoinRefresh';
			pView.registerEventTransactionAsyncOperation(pTransactionGUID, tmpRefreshEvent);
			this.pict.HeadlightRestClient.getJSON(`SampleLabJoins/FilteredTo/FBV~IDSample~EQ~${tmpSampleRecord.IDSample}`, (pError, pRecords) =>
			{
				// A failed fetch is not evidence of "no labs". Treating it as an empty set and writing that
				// through overwrites the persisted selection on a transient network error -- and the document
				// auto-save then persists the loss with no user action. Abort instead: leave both the joins
				// address and the input value exactly as loaded.
				if (pError || !Array.isArray(pRecords))
				{
					this.log.error(`MaterialsLabPicker: failed to load SampleLabJoins for Sample ID ${tmpSampleRecord.IDSample} -- keeping the persisted value.`, { pError, pRecords });
					pView.eventTransactionAsyncOperationComplete(pTransactionGUID, tmpRefreshEvent);
					return;
				}
				const tmpLabValue = pRecords.map((pJoin) => pJoin.IDLab).join(',');
				// Fail-open backstop, same instinct as the entity selector's autofill-scope vet: an empty result
				// cannot be distinguished from "the joins could not be resolved", so it must not clobber a value
				// the document already carries. Only an explicit user action may empty this. A non-empty result
				// is authoritative and still corrects a stale selection.
				const tmpExistingValue = this.pict.manifest.getValueAtAddress(
					pView.getMarshalDestinationObject(), this._getRecordAddress(pInput, pRowIndex, ''));
				if (tmpLabValue === '' && String(tmpExistingValue ?? '').trim() !== '')
				{
					this._dropSoftDeletedHeldJoins(pView, pInput, pRowIndex,
						{ Entity: 'SampleLabJoin', ValueField: 'IDLab', JoinAddress: tmpSampleLabJoinAddress, Value: tmpExistingValue, Label: 'MaterialsLabPicker' },
						(pDropped) =>
						{
							if (pDropped < 1)
							{
								this.log.warn(`MaterialsLabPicker: Sample ID ${tmpSampleRecord.IDSample} resolved no SampleLabJoins but the document carries [${tmpExistingValue}] -- keeping the persisted value.`);
							}
							pView.eventTransactionAsyncOperationComplete(pTransactionGUID, tmpRefreshEvent);
						});
					return;
				}
				this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleLabJoinAddress, pRecords);
				// Re-binding to a different Sample replaces this input's joins wholesale; the outgoing sample's
				// joins stay attached to that sample and are not this form's to delete.
				ScopedEntityData.beginProgrammaticLoad(this.pict);
				try
				{
					this._setValueAndCommit(pView, pInput, tmpLabValue, pRowIndex);
				}
				finally
				{
					ScopedEntityData.endProgrammaticLoad(this.pict);
				}
				pView.eventTransactionAsyncOperationComplete(pTransactionGUID, tmpRefreshEvent);
			});
			return;
		}

		if (tmpEventType === libLabRouting.SAMPLE_LAB_SYNC_EVENT)
		{
			const tmpNextValue = libLabRouting.syncSampleLabSelection(this.pict, pInput, pView.getMarshalDestinationObject(),
				this._getRecordAddress(pInput, null, '__Promoted'), pView.getValueByHash(pInput.Hash));
			if (tmpNextValue !== null)
			{
				this._setValueAndCommit(pView, pInput, tmpNextValue, null);
			}
			return;
		}

		if (tmpEventType === 'ProjectEntityDataRecords')
		{
			const tmpTarget = this._getProjectionTarget(pInput, pEvent);
			if (!tmpTarget)
			{
				return;
			}
			this._syncRecordsToEntityData(pView, pInput, pRowIndex, pTransactionGUID, tmpTarget);
			const tmpLabAddress = this._getRecordAddress(pInput, pRowIndex);
			const tmpDocumentPolyJoinAddress = pInput.PictForm.DocumentPolyJoinAddress || `${tmpLabAddress}_DocumentPolyJoin`;
			const tmpDocumentPolyJoin = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpDocumentPolyJoinAddress);
			if (tmpDocumentPolyJoin && typeof tmpDocumentPolyJoin === 'object' && !Array.isArray(tmpDocumentPolyJoin) && tmpTarget && typeof tmpTarget === 'object' && !Array.isArray(tmpTarget))
			{
				if (!tmpTarget.DocumentPolyJoin || typeof tmpTarget.DocumentPolyJoin !== 'object' || Array.isArray(tmpTarget.DocumentPolyJoin))
				{
					tmpTarget.DocumentPolyJoin = { };
				}
				tmpTarget.DocumentPolyJoin[tmpDocumentPolyJoin.GUIDDocumentPolyJoin] = tmpDocumentPolyJoin;
			}
		}
	}

	/**
	 * Project the SampleLabJoin array into the entity-data collection (keyed by GUIDSampleLabJoin) —
	 * the plural counterpart of the base _syncRecordToEntityData (the Lab CATALOG record itself never
	 * projects; the joins are the created data). Ported 1:1.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} pRowIndex
	 * @param {string} pTransactionGUID @param {Object} pTargetObject
	 */
	_syncRecordsToEntityData(pView, pInput, pRowIndex, pTransactionGUID, pTargetObject)
	{
		const tmpLabAddress = this._getRecordAddress(pInput, pRowIndex);
		const tmpEntityData = pTargetObject;
		if (!tmpEntityData || typeof tmpEntityData !== 'object' || Array.isArray(tmpEntityData))
		{
			return;
		}

		const tmpSampleLabJoinsAddress = pInput.PictForm.SampleLabJoinAddress || `${tmpLabAddress}_SampleJoins`;
		const tmpSampleLabJoins = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleLabJoinsAddress);
		if (Array.isArray(tmpSampleLabJoins))
		{
			if (!tmpEntityData.SampleLabJoin || typeof tmpEntityData.SampleLabJoin !== 'object' || Array.isArray(tmpEntityData.SampleLabJoin))
			{
				tmpEntityData.SampleLabJoin = { };
			}
			for (const tmpSampleLabJoin of tmpSampleLabJoins)
			{
				tmpEntityData.SampleLabJoin[tmpSampleLabJoin.GUIDSampleLabJoin] = tmpSampleLabJoin;
			}
		}
	}

	/**
	 * The bound Sample record: ask the family over the bus (PushRecord into TempData), falling back to
	 * the conventional SampleAddress. Ported 1:1 (pView may be absent on a BaseFilter-closure call —
	 * the bus path needs no view).
	 * @param {Object|null} pView @param {Record<string, any>} pInput @return {Record<string, any>|null}
	 */
	_getBoundSampleRecord(pView, pInput)
	{
		const tmpPayloadUUID = this.pict.getUUID();
		this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`PushRecord:${this._getTrackingGUID(pInput)}:TempData["${tmpPayloadUUID}"]`);

		let tmpSample = this.pict.TempData[tmpPayloadUUID];
		delete this.pict.TempData[tmpPayloadUUID];

		if (!tmpSample || typeof tmpSample !== 'object' || Array.isArray(tmpSample))
		{
			//FIXME: what we actually need is a way to find this via the tracking GUID
			if (!pView || typeof pView.getMarshalDestinationObject !== 'function')
			{
				return null;
			}
			const tmpSampleAddress = pInput.PictForm.SampleAddress || 'SampleID__Record';
			tmpSample = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleAddress);
		}
		return tmpSample;
	}
}

module.exports = PictProviderInputTypeMaterialsLabPicker;

module.exports.default_configuration = _DEFAULT_CONFIGURATION;
